import * as React from 'react';
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
} from 'echarts/components';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { SVGRenderer } from 'echarts/renderers';
import type {
  GridComponentOption,
  LegendComponentOption,
  LineSeriesOption,
  TitleComponentOption,
  TooltipComponentOption,
} from 'echarts';
import type { AppDownloadPeriod, AppDownloadTrendRow } from './appDownloadApi';

type ECOption = echarts.ComposeOption<
  | LineSeriesOption
  | TitleComponentOption
  | TooltipComponentOption
  | GridComponentOption
  | LegendComponentOption
>;

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  LineChart,
  LabelLayout,
  UniversalTransition,
  SVGRenderer,
]);

const AppDownloadTrendChart: React.FC<{
  data: AppDownloadTrendRow[];
  period: AppDownloadPeriod;
}> = ({ data, period }) => {
  const chartRef = React.useRef<HTMLDivElement>(null);
  const chartInstance = React.useRef<echarts.ECharts | null>(null);

  React.useEffect(() => {
    if (!chartRef.current) return;
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }
    const labels = data.map((row) => row.period);
    const option: ECOption = {
      tooltip: { trigger: 'axis' },
      legend: { data: ['Android', 'iOS', 'Total'] },
      grid: { left: 48, right: 24, top: 40, bottom: 40 },
      xAxis: { type: 'category', data: labels, name: period },
      yAxis: { type: 'value', name: 'Downloads' },
      series: [
        {
          name: 'Android',
          type: 'line',
          stack: undefined,
          data: data.map((row) => row.android_downloads),
          color: '#3DDC84',
        },
        {
          name: 'iOS',
          type: 'line',
          data: data.map((row) => row.ios_downloads),
          color: '#555555',
        },
        {
          name: 'Total',
          type: 'line',
          data: data.map((row) => row.total_downloads),
          color: '#1976d2',
        },
      ],
    };
    chartInstance.current.setOption(option, true);
    const resizeHandler = () => chartInstance.current?.resize();
    window.addEventListener('resize', resizeHandler);
    return () => {
      window.removeEventListener('resize', resizeHandler);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, [data, period]);

  return <div ref={chartRef} style={{ width: '100%', height: 360 }} />;
};

export default AppDownloadTrendChart;
